class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.summaryToggle = this.mainDetailsToggle.querySelector('summary');
    this.submenu = this.mainDetailsToggle.querySelector('.header__submenu-contents');
    this.disclosureContainer = this.closest('.header__menu-disclosure-item') || this;
    this.topLevelLink = this.disclosureContainer.querySelector(':scope > .header__menu-main-link');
    this.focusableElements = [];

    this.disclosureContainer.addEventListener('keydown', this.onKeyDown.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    this.disclosureContainer.addEventListener('focusout', this.onFocusOut.bind(this));
    this.disclosureContainer.addEventListener('mouseover', this.onHover.bind(this));
  }

  onKeyDown(event) {
    const eventKey = this.getNormalizedKey(event);

    if (eventKey === 'ESCAPE') {
      event.preventDefault();
      this.close();
      return;
    }

    const isDisclosureControl = this.summaryToggle.contains(event.target);
    const isTopLevelLink = this.topLevelLink?.contains(event.target);
    const isSubmenuItem = this.submenu?.contains(event.target);

    if (!isDisclosureControl && !isTopLevelLink && !isSubmenuItem) return;

    if (isDisclosureControl && (eventKey === 'ENTER' || eventKey === 'SPACE')) {
      event.preventDefault();
      this.mainDetailsToggle.hasAttribute('open') ? this.close() : this.open();
      return;
    }

    if (isTopLevelLink && eventKey === 'SPACE') {
      event.preventDefault();
      this.mainDetailsToggle.hasAttribute('open') ? this.close() : this.open();
      return;
    }

    if (eventKey === 'ARROWDOWN') {
      event.preventDefault();
      this.open();
      this.focusNextSubmenuElement(event.target);
      return;
    }

    if (eventKey === 'ARROWUP') {
      event.preventDefault();
      this.open();
      this.focusPreviousSubmenuElement(isSubmenuItem ? event.target : null);
    }
  }

  onToggle() {
    const isOpen = this.mainDetailsToggle.hasAttribute('open');

    if (isOpen) {
      this.closeSiblingDisclosures();
      this.addCloseListeners();
    } else {
      this.removeCloseListeners();
    }

    this.summaryToggle.setAttribute('aria-expanded', isOpen);
    this.topLevelLink?.setAttribute('aria-expanded', isOpen);
    this.submenu?.setAttribute('aria-hidden', !isOpen);
    this.classList.toggle('is-open', isOpen);
    this.disclosureContainer.classList.toggle('is-open', isOpen);
  }

  onFocusOut(event) {
    if (!this.disclosureContainer.contains(event.relatedTarget)) this.close(false);
  }

  onHover(event) {
    event.preventDefault();
    this.open();
  }

  onMenuClose(event) {
    if (!this.disclosureContainer.contains(event.target)) this.close(false);
  }

  open() {
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.onToggle();
      return;
    }

    this.mainDetailsToggle.setAttribute('open', true);
  }

  close(focusToggle = true) {
    if (focusToggle && this.disclosureContainer.contains(document.activeElement)) {
      (this.topLevelLink || this.summaryToggle).focus();
    }

    this.mainDetailsToggle.removeAttribute('open');
    this.onToggle();
  }

  closeSiblingDisclosures() {
    this.closest('nav')?.querySelectorAll('details').forEach((details) => {
      if (details === this.mainDetailsToggle) return;
      const disclosure = details.closest('details-disclosure');

      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', false);
      details.querySelector('.header__submenu-contents')?.setAttribute('aria-hidden', true);
      details.closest('.header__menu-disclosure-item')?.querySelector(':scope > .header__menu-main-link')?.setAttribute('aria-expanded', false);
      disclosure?.removeCloseListeners?.();
      disclosure?.classList.remove('is-open');
      details.closest('.header__menu-disclosure-item')?.classList.remove('is-open');
    });
  }

  addCloseListeners() {
    this.onMenuCloseEvent =
      this.onMenuCloseEvent || this.onMenuClose.bind(this);
    document.body.addEventListener('click', this.onMenuCloseEvent);
    document.body.addEventListener('mouseover', this.onMenuCloseEvent);
  }

  removeCloseListeners() {
    document.body.removeEventListener('click', this.onMenuCloseEvent);
    document.body.removeEventListener('mouseover', this.onMenuCloseEvent);
  }

  getFocusableSubmenuElements() {
    this.focusableElements = this.submenu ? getFocusableElements(this.submenu) : [];
    return this.focusableElements;
  }

  focusNextSubmenuElement(currentElement) {
    const focusableElements = this.getFocusableSubmenuElements();
    if (!focusableElements.length) return;

    const currentIndex = focusableElements.indexOf(currentElement);
    const nextElement = focusableElements[currentIndex + 1] || focusableElements[0];
    nextElement.focus();
  }

  focusPreviousSubmenuElement(currentElement) {
    const focusableElements = this.getFocusableSubmenuElements();
    if (!focusableElements.length) return;

    const currentIndex = focusableElements.indexOf(currentElement);
    const previousElement = currentIndex > 0 ? focusableElements[currentIndex - 1] : focusableElements[focusableElements.length - 1];
    previousElement.focus();
  }

  getNormalizedKey(event) {
    if (event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space') return 'SPACE';
    return (event.key || event.code || '').toUpperCase();
  }
}

customElements.define('details-disclosure', DetailsDisclosure);
